import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Users, Plus, Edit, Trash2, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TeamManagement = () => {
  const [commercials, setCommercials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'Commercial',
    adresse_personnelle: '',
    code_postal_personnel: '',
    ville_personnelle: ''
  });

  useEffect(() => {
    fetchCommercials();
  }, []);

  const fetchCommercials = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrer uniquement les commerciaux
      const commercialsOnly = response.data.filter(u => u.role === 'Commercial');
      setCommercials(commercialsOnly);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching commercials:', error);
      toast.error('Erreur lors du chargement des commerciaux');
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'Commercial',
        adresse_personnelle: user.adresse_personnelle || '',
        code_postal_personnel: user.code_postal_personnel || '',
        ville_personnelle: user.ville_personnelle || ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: 'Commercial',
        adresse_personnelle: '',
        code_postal_personnel: '',
        ville_personnelle: ''
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingUser) {
        // Update existing user
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password; // Ne pas envoyer si vide
        
        await axios.put(`${API}/users/${editingUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Commercial mis à jour avec succès');
      } else {
        // Create new user
        if (!formData.password) {
          toast.error('Le mot de passe est requis pour un nouvel utilisateur');
          return;
        }
        
        await axios.post(`${API}/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Commercial créé avec succès');
      }
      
      setShowDialog(false);
      fetchCommercials();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce commercial ?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Commercial supprimé avec succès');
      fetchCommercials();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredCommercials = commercials.filter(c =>
    `${c.first_name} ${c.last_name} ${c.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Gestion d'Équipe</h1>
          <p className="text-gray-600">Gérer les commerciaux de votre équipe</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Commercial
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Équipe Commerciale ({filteredCommercials.length})
          </CardTitle>
          <CardDescription>
            Liste des commerciaux sous votre responsabilité
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Rechercher un commercial..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Chargement...</div>
          ) : filteredCommercials.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucun commercial trouvé
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Adresse</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCommercials.map((commercial) => (
                  <TableRow key={commercial.id}>
                    <TableCell className="font-medium">
                      {commercial.first_name} {commercial.last_name}
                    </TableCell>
                    <TableCell>{commercial.email}</TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {commercial.adresse_personnelle ? (
                        <div>
                          {commercial.adresse_personnelle}
                          <br />
                          {commercial.code_postal_personnel} {commercial.ville_personnelle}
                        </div>
                      ) : (
                        <span className="text-gray-400">Non renseignée</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={commercial.is_active ? 'success' : 'secondary'}>
                        {commercial.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDialog(commercial)}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Modifier
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(commercial.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Supprimer
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Création/Modification */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Modifier le Commercial' : 'Nouveau Commercial'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Prénom"
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Nom"
                />
              </div>
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                disabled={!!editingUser}
              />
            </div>

            <div>
              <Label>Mot de passe {!editingUser && '*'}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={editingUser ? 'Laisser vide pour ne pas changer' : 'Mot de passe'}
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">Adresse Personnelle</h4>
              <div className="space-y-3">
                <div>
                  <Label>Adresse</Label>
                  <Input
                    value={formData.adresse_personnelle}
                    onChange={(e) => setFormData({ ...formData, adresse_personnelle: e.target.value })}
                    placeholder="Numéro et rue"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Code Postal</Label>
                    <Input
                      value={formData.code_postal_personnel}
                      onChange={(e) => setFormData({ ...formData, code_postal_personnel: e.target.value })}
                      placeholder="75001"
                    />
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input
                      value={formData.ville_personnelle}
                      onChange={(e) => setFormData({ ...formData, ville_personnelle: e.target.value })}
                      placeholder="Paris"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
              {editingUser ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
